import * as React from "react"
import { useRouteMatch } from "react-router-dom"
import { useClickAway } from "react-use"
import { svgIconStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgChevronDownSolid } from "@digits-shared/components/SVGIcons/solid/ChevronDownSolid.svg"
import { ColorIcon } from "@digits-shared/components/UI/Elements/ColorIcon"
import stringHelper from "@digits-shared/helpers/stringHelper"
import userHelper from "@digits-shared/helpers/userHelper"
import useForwardedRef from "@digits-shared/hooks/useForwardedRef"
import { useMeasure } from "@digits-shared/hooks/useMeasure"
import useSession from "@digits-shared/hooks/useSession"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import { SidebarMenu } from "src/frontend/components/Shared/NavSidebar/Menu/SidebarMenu"
import { SidebarFullDivider } from "src/frontend/components/Shared/NavSidebar/sidebarConstants"
import { SidebarRow } from "src/frontend/components/Shared/NavSidebar/SidebarExpando"
import { useNavSidebarContext } from "src/frontend/components/Shared/NavSidebar/useNavSidebarContext"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"
import { EntityIcon } from "src/shared/components/ObjectEntities/EntityIcon"

/*
  STYLES
*/

const Header = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 12px auto;
  // this min-height prevents shift when switching between operator LEs and accounting firms
  // since an affiliated LE will show the LE name *and* the accounting firm name
  min-height: 48px;

  .digits-mac & {
    padding-top: 40px;
  }
`

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  position: relative;
`

const BusinessInfo = styled.div`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const BusinessName = styled.div`
  font-weight: ${fonts.weight.heavy};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const BusinessOrg = styled.div`
  font-size: 12px;
  font-weight: ${fonts.weight.normal};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const BusinessIcon = styled(EntityIcon)`
  margin: 0 0 0 -2px;
  padding: 0;
`

const Chevron = styled(SvgChevronDownSolid)`
  width: 20px;
  height: 20px;
  margin: -2px 0;
  ${svgIconStyles(colors.secondary)};

  transition: transform 250ms;
  .header-open & {
    transform: rotate(-180deg);
  }
`

/*
  COMPONENT
*/

export const SidebarHeader: React.FC<{ isShareUser?: boolean }> = ({ isShareUser }) => {
  const { isHeaderMenuOpen, hideHeaderMenu } = useNavSidebarContext()

  const [measureRef, { top, height }] = useMeasure<HTMLDivElement>()
  const headerRef = useForwardedRef(measureRef)
  const menuRef = React.useRef<HTMLDivElement>(null)
  useClickAway(menuRef, (e) => {
    let menu = e.target as HTMLElement | null
    while (menu) {
      if (menu === headerRef.current) return
      menu = menu.parentElement
    }
    hideHeaderMenu()
  })

  return (
    <>
      <Header
        ref={headerRef}
        className={stringHelper.classNames(isHeaderMenuOpen && "header-open")}
      >
        <HeaderRow />
        <SidebarMenu
          ref={menuRef}
          headerTop={top + height}
          isOpen={isHeaderMenuOpen}
          hideMenu={hideHeaderMenu}
          isShareUser={isShareUser}
        />
      </Header>

      <SidebarFullDivider />
    </>
  )
}

const HeaderRow: React.FC = () => {
  const {
    user,
    currentLegalEntity,
    currentAffiliation,
    currentAffiliatedOrganization,
    currentOrganization,
  } = useSession<FrontendSession>()

  const isFirmFocused = useRouteMatch(routes.firmDashboardRoot.parameterizedPath)

  const legalEntityOrOrg = isFirmFocused
    ? currentOrganization
    : currentAffiliation?.entity || currentLegalEntity

  const iconUrl = isFirmFocused
    ? currentOrganization?.iconUrl
    : (currentAffiliatedOrganization || currentOrganization)?.iconUrl

  const { toggleHeaderMenu } = useNavSidebarContext()

  if (!user) return null

  // SHARED USER
  if (!legalEntityOrOrg) {
    return (
      <SidebarRow
        rowType="button"
        onClick={toggleHeaderMenu}
        element={
          <ColorIcon imageUrl={user.avatarUrl} fallbackUser={user} size={24} fontSize={12} />
        }
      >
        <HeaderTitle>
          <BusinessInfo>
            <BusinessName>{userHelper.displayName(user)}</BusinessName>
          </BusinessInfo>
          <Chevron />
        </HeaderTitle>
      </SidebarRow>
    )
  }

  return (
    <SidebarRow
      rowType="button"
      onClick={toggleHeaderMenu}
      element={
        <BusinessIcon imageUrl={iconUrl} size={24}>
          {legalEntityOrOrg?.name}
        </BusinessIcon>
      }
    >
      <HeaderTitle>
        <BusinessInfo>
          <BusinessName>{legalEntityOrOrg?.name}</BusinessName>
          {!isFirmFocused && currentAffiliatedOrganization && currentOrganization && (
            <BusinessOrg>{currentOrganization.name}</BusinessOrg>
          )}
        </BusinessInfo>
        <Chevron />
      </HeaderTitle>
    </SidebarRow>
  )
}
